import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bus, MapPin, Navigation, Play, Square, Loader2, 
  Wifi, WifiOff, LogOut, Gauge, Clock, AlertCircle,
  Smartphone, CheckCircle, XCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverTrackingApp = () => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [driver, setDriver] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchParams] = useSearchParams();
  const [school, setSchool] = useState(null);
  
  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Tracking state
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [heading, setHeading] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [updateCount, setUpdateCount] = useState(0);
  
  // Refs
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);

  // Get school from URL param
  const schoolFromUrl = searchParams.get('school');
  const schoolCode = schoolFromUrl || localStorage.getItem('school_code') || null;

  // Fetch school info
  useEffect(() => {
    if (schoolCode) {
      localStorage.setItem('school_code', schoolCode);
      localStorage.setItem('login_source', `/${schoolCode}`);
      fetchSchoolInfo();
    }
  }, [schoolCode]);

  const fetchSchoolInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/schools/public/${schoolCode}`);
      setSchool(response.data);
    } catch (error) {
      console.error('Failed to fetch school:', error);
    }
  };

  // Check for saved session
  useEffect(() => {
    const savedToken = localStorage.getItem('driver_token');
    const savedDriver = localStorage.getItem('driver_data');
    const savedVehicle = localStorage.getItem('selected_vehicle');
    
    if (savedToken && savedDriver) {
      setAuthToken(savedToken);
      setDriver(JSON.parse(savedDriver));
      setIsLoggedIn(true);
      if (savedVehicle) {
        setSelectedVehicle(JSON.parse(savedVehicle));
      }
    }
  }, []);

  // Fetch vehicles when logged in
  useEffect(() => {
    if (isLoggedIn && authToken) {
      fetchVehicles();
    }
  }, [isLoggedIn, authToken]);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/vehicles?status=active`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setVehicles(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { token, user } = res.data;
      
      // Check if user is a driver (either by role or is_driver flag for staff)
      if (user.role !== 'driver' && !user.is_driver) {
        toast.error(`This app is for drivers only. Your account is: ${user.role}. Please contact admin to set up your driver account.`);
        setLoginLoading(false);
        return;
      }
      
      setAuthToken(token);
      setDriver(user);
      setIsLoggedIn(true);
      
      // Save session
      localStorage.setItem('driver_token', token);
      localStorage.setItem('driver_data', JSON.stringify(user));
      
      toast.success('Login successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Check your email and password.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    stopTracking();
    setIsLoggedIn(false);
    setAuthToken(null);
    setDriver(null);
    setSelectedVehicle(null);
    setVehicles([]);
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_data');
    localStorage.removeItem('selected_vehicle');
    toast.success('Logged out');
  };

  const selectVehicle = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    setSelectedVehicle(vehicle);
    if (vehicle) {
      localStorage.setItem('selected_vehicle', JSON.stringify(vehicle));
    }
  };

  const sendLocationUpdate = useCallback(async (position) => {
    if (!selectedVehicle || !authToken) return;
    
    const { latitude, longitude, speed: gpsSpeed, heading: gpsHeading, accuracy: gpsAccuracy } = position.coords;
    
    // Update local state
    setCurrentLocation({ lat: latitude, lng: longitude });
    setSpeed(gpsSpeed ? Math.round(gpsSpeed * 3.6) : 0); // m/s to km/h
    setHeading(gpsHeading);
    setAccuracy(gpsAccuracy ? Math.round(gpsAccuracy) : null);
    setLastUpdate(new Date());
    setLocationError(null);
    
    try {
      await axios.post(`${API_URL}/api/transport-v2/gps/update-location`, {
        vehicle_id: selectedVehicle.id,
        latitude,
        longitude,
        speed_kmh: gpsSpeed ? Math.round(gpsSpeed * 3.6) : 0,
        heading: gpsHeading || null,
        accuracy: gpsAccuracy || null
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      setUpdateCount(prev => prev + 1);
    } catch (error) {
      console.error('Failed to send location:', error);
    }
  }, [selectedVehicle, authToken]);

  const handleLocationError = useCallback((error) => {
    let message = 'Location error';
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message = 'Location permission denied. Please enable GPS.';
        break;
      case error.POSITION_UNAVAILABLE:
        message = 'Location unavailable. Check GPS settings.';
        break;
      case error.TIMEOUT:
        message = 'Location request timed out.';
        break;
      default:
        message = 'Unknown location error';
    }
    setLocationError(message);
    toast.error(message);
  }, []);

  const startTracking = async () => {
    if (!selectedVehicle) {
      toast.error('Please select a vehicle first');
      return;
    }
    
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    // Request location permission
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      if (permission.state === 'denied') {
        toast.error('Location permission denied. Please enable in browser settings.');
        return;
      }
    } catch (e) {
      // Permission API not supported, continue anyway
    }
    
    setIsTracking(true);
    setUpdateCount(0);
    
    // Start watching position
    watchIdRef.current = navigator.geolocation.watchPosition(
      sendLocationUpdate,
      handleLocationError,
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
    
    // Also send update every 10 seconds even if position hasn't changed significantly
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        sendLocationUpdate,
        handleLocationError,
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }, 10000);
    
    // Notify server that tracking started
    try {
      await axios.post(`${API_URL}/api/transport-v2/gps/start-tracking/${selectedVehicle.id}`, null, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (e) {
      console.error('Failed to notify server');
    }
    
    toast.success('Tracking started!');
  };

  const stopTracking = async () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setIsTracking(false);
    
    // Notify server that tracking stopped
    if (selectedVehicle && authToken) {
      try {
        await axios.post(`${API_URL}/api/transport-v2/gps/stop-tracking/${selectedVehicle.id}`, null, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      } catch (e) {
        console.error('Failed to notify server');
      }
    }
    
    toast.success('Tracking stopped');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const formatTime = (date) => {
    if (!date) return '--:--:--';
    return date.toLocaleTimeString();
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            {school && (school.logo_url || school.logo) ? (
              <div className="bg-white p-3 rounded-2xl inline-block mb-4">
                <img src={school.logo_url || school.logo} alt={school.name} className="h-16 w-16 object-contain" />
              </div>
            ) : (
              <div className="bg-white/20 p-4 rounded-full inline-block mb-4">
                <Bus className="h-12 w-12 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-white">{school?.name || 'Driver Tracking App'}</h1>
            <p className="text-blue-100 mt-2">Share your location with school</p>
          </div>

          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle>Driver Login</CardTitle>
              <CardDescription>Enter your school credentials</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="driver@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in...</>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-blue-100 text-sm mt-6">
            {school?.name || 'Cloud Campus Schools'} • GPS Tracking
          </p>
        </motion.div>
      </div>
    );
  }

  // Main Tracking Screen
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bus className="h-8 w-8" />
            <div>
              <h1 className="font-bold">Driver Tracking</h1>
              <p className="text-sm text-blue-100">{driver?.name}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-blue-700">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Vehicle Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bus className="h-4 w-4" /> Select Vehicle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={selectedVehicle?.id || ''} 
              onValueChange={selectVehicle}
              disabled={isTracking}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose your vehicle" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map(v => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.vehicle_number} ({v.vehicle_type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedVehicle && (
              <p className="text-sm text-muted-foreground mt-2">
                Route: {selectedVehicle.route_name || 'Not assigned'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Tracking Status */}
        <Card className={isTracking ? 'border-green-500 border-2' : ''}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Navigation className="h-4 w-4" /> Tracking Status
              </CardTitle>
              {isTracking ? (
                <Badge className="bg-green-500">
                  <Wifi className="h-3 w-3 mr-1" /> LIVE
                </Badge>
              ) : (
                <Badge variant="secondary">
                  <WifiOff className="h-3 w-3 mr-1" /> OFF
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Start/Stop Button */}
            {isTracking ? (
              <Button 
                variant="destructive" 
                className="w-full h-16 text-lg"
                onClick={stopTracking}
              >
                <Square className="h-6 w-6 mr-2" /> Stop Tracking
              </Button>
            ) : (
              <Button 
                className="w-full h-16 text-lg bg-green-600 hover:bg-green-700"
                onClick={startTracking}
                disabled={!selectedVehicle}
              >
                <Play className="h-6 w-6 mr-2" /> Start Tracking
              </Button>
            )}

            {/* Location Info */}
            {isTracking && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-3"
              >
                {locationError ? (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                      <XCircle className="h-5 w-5" />
                      <span className="text-sm">{locationError}</span>
                    </div>
                  </div>
                ) : currentLocation ? (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Location Active</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{currentLocation.lat.toFixed(5)}, {currentLocation.lng.toFixed(5)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Gauge className="h-3 w-3" />
                        <span>{speed} km/h</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatTime(lastUpdate)}</span>
                      </div>
                      {accuracy && (
                        <div className="flex items-center gap-1">
                          <Navigation className="h-3 w-3" />
                          <span>±{accuracy}m</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Getting location...</span>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Updates sent: {updateCount}</span>
                  <span>Interval: 10s</span>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4" /> Instructions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Select your assigned vehicle</li>
              <li>Tap <strong>"Start Tracking"</strong> before starting your trip</li>
              <li>Keep this app open during your trip</li>
              <li>Tap <strong>"Stop Tracking"</strong> when trip ends</li>
              <li>Parents can see your live location on their app</li>
            </ol>
          </CardContent>
        </Card>

        {/* Tips */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300">Tips for better tracking:</p>
              <ul className="mt-1 space-y-1 text-blue-700 dark:text-blue-400">
                <li>• Keep GPS/Location enabled</li>
                <li>• Keep screen on or use battery saver mode</li>
                <li>• Ensure good network connectivity</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverTrackingApp;
